import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  FileWarning,
  X,
} from "lucide-react";
import ScreenOverlay from "./common/ScreenOverlay";

function money(value) {
  if (value === null || value === undefined) return "—";

  return Number(value).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function dateText(value) {
  if (!value) return "—";

  const raw = String(value).slice(0, 10);
  const [year, month, day] = raw.split("-");

  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
}

export default function OverdueBillingModal({
  open,
  invoices = [],
  onClose,
}) {
  const navigate = useNavigate();

  const summary = useMemo(() => {
    const total = invoices.reduce(
      (sum, item) => sum + Number(item?.amount || 0),
      0
    );

    const oldest = [...invoices].sort((a, b) =>
      String(a?.due_date || "").localeCompare(
        String(b?.due_date || "")
      )
    )[0];

    return {
      count: invoices.length,
      total,
      oldest,
    };
  }, [invoices]);

  if (!open) return null;

  function goToFinancial() {
    onClose?.();

    navigate(
      "/app/meu-plano?tab=financial"
    );
  }

  return (
    <ScreenOverlay><div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      data-testid="overdue-billing-modal"
    >
      <button
        type="button"
        aria-label="Fechar aviso"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-[3px] animate-in fade-in duration-200"
      />

      <div
        className="
          relative z-10 w-full max-w-[430px]
          overflow-hidden rounded-[28px]
          border border-white/70 bg-white
          shadow-[0_35px_100px_rgba(15,23,42,0.32)]
          animate-in fade-in zoom-in-95 slide-in-from-bottom-3
          duration-300
        "
      >
        <div className="relative px-7 pb-2 pt-8">
          <button
            type="button"
            onClick={onClose}
            className="
              absolute right-4 top-4
              inline-flex h-9 w-9 items-center justify-center
              rounded-full text-slate-400
              transition-all duration-200
              hover:bg-slate-100 hover:text-slate-700
            "
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex justify-center">
            <div
              className="
                relative flex h-[74px] w-[74px]
                items-center justify-center
                rounded-full
                bg-gradient-to-br
                from-amber-400 to-orange-500
                shadow-[0_14px_35px_rgba(249,115,22,0.30)]
              "
            >
              <FileWarning className="h-8 w-8 text-white" />

              <span
                className="
                  absolute -right-1 -top-1
                  flex h-6 min-w-6 items-center justify-center
                  rounded-full border-[3px] border-white
                  bg-rose-500 px-1
                  text-[10px] font-bold text-white
                "
              >
                {summary.count}
              </span>
            </div>
          </div>

          <div className="mt-5 text-center">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-600">
              Atenção financeira
            </div>

            <h2 className="mt-2 text-[21px] font-semibold tracking-tight text-slate-950">
              Boletos em atraso
            </h2>

            <p className="mx-auto mt-2 max-w-[330px] text-[13px] leading-5 text-slate-500">
              Identificamos cobranças vencidas vinculadas à sua conta.
              Regularize os pagamentos para manter sua situação financeira em dia.
            </p>
          </div>
        </div>

        <div className="px-6 py-5">
          <div
            className="
              overflow-hidden rounded-2xl
              border border-slate-200
              bg-slate-50/80
            "
          >
            <div className="grid grid-cols-2 divide-x divide-slate-200">
              <div className="p-4 text-center">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                  Em atraso
                </div>

                <div className="mt-1 text-lg font-semibold text-slate-950">
                  {summary.count}
                </div>

                <div className="text-[11px] text-slate-500">
                  {summary.count === 1 ? "cobrança" : "cobranças"}
                </div>
              </div>

              <div className="p-4 text-center">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                  Valor vencido
                </div>

                <div className="mt-1 text-lg font-semibold text-slate-950">
                  {money(summary.total)}
                </div>

                <div className="text-[11px] text-slate-500">
                  valor pendente
                </div>
              </div>
            </div>

            {summary.oldest?.due_date ? (
              <div className="flex items-center gap-2 border-t border-slate-200 px-4 py-3 text-xs text-slate-500">
                <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />
                Vencimento mais antigo:
                <span className="font-medium text-slate-700">
                  {dateText(summary.oldest.due_date)}
                </span>
              </div>
            ) : null}
          </div>

          <div className="mt-5 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="
                h-11 flex-1 rounded-xl
                border border-slate-200 bg-white
                text-sm font-medium text-slate-600
                transition-all duration-200
                hover:bg-slate-50
              "
            >
              Fechar
            </button>

            <button
              type="button"
              onClick={goToFinancial}
              className="
                inline-flex h-11 flex-[1.35]
                items-center justify-center gap-2
                rounded-xl
                bg-gradient-to-r from-violet-600 to-indigo-600
                px-4 text-sm font-semibold text-white
                shadow-[0_8px_22px_rgba(109,40,217,0.22)]
                transition-all duration-200
                hover:-translate-y-0.5
                hover:shadow-[0_12px_28px_rgba(109,40,217,0.30)]
              "
            >
              Ver débitos
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="h-1 bg-gradient-to-r from-violet-600 via-indigo-500 to-violet-600" />
      </div>
    </div></ScreenOverlay>
  );
}
